/**
 * Canonical builder for the gateway `description` string — the human-readable
 * text sent to Moyasar/HyperPay/Ziina/Tap/Stripe that is stored on the payment
 * and rendered verbatim in the admin Payments list.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every Apple Pay button used to hand-roll this string client-side while the
 * card path built its own in a server service, and the two were never
 * reconciled. That produced four different texts for a single court booking,
 * word-order inverses for package balances (`X - Package Payment` vs
 * `Package Payment - X`), a bare package name for Apple Pay renewals, and —
 * worst — card-paid CLASS registrations described as `Court booking for …`,
 * because the class action reuses createCourtBookingCheckout and that service
 * hardcoded court wording. Two surfaces also leaked a raw UUID slice.
 *
 * Route every gateway description through this module so the ledger stays
 * consistent no matter which surface or payment method produced the charge.
 *
 * THIS IS LEDGER DATA, NOT UI COPY — keep it English and never translate it.
 * A `t()`-built description stores whatever language the payer's browser
 * happened to be in, which makes the Payments column unfilterable and leaks
 * Arabic into reconciliation exports. Customers still see localized text in
 * the checkout UI itself; this field is for the merchant's records.
 *
 * Client-safe on purpose: imported by the `'use client'` Apple Pay buttons as
 * well as server services, so this module must never import `server-only` or
 * anything that reaches for a database. It is pure string formatting.
 */

/** Payment surfaces that can produce a charge. */
export type PaymentDescriptionSurface =
  | 'court_booking'
  | 'class_registration'
  | 'package_purchase'
  | 'package_renewal'
  | 'package_balance'
  | 'membership_purchase'
  | 'membership_balance'
  | 'split_payment'
  | 'product_sale'
  | 'guest_pass'
  | 'wallet_topup'
  | 'platform_subscription';

/** Human-readable label per surface. Keep these stable — they are ledger text. */
const SURFACE_LABELS: Record<PaymentDescriptionSurface, string> = {
  court_booking: 'Court Booking',
  class_registration: 'Class Registration',
  package_purchase: 'Package Purchase',
  package_renewal: 'Package Renewal',
  package_balance: 'Package Balance',
  membership_purchase: 'Membership',
  membership_balance: 'Membership Balance',
  split_payment: 'Split Payment',
  product_sale: 'Product Sale',
  guest_pass: 'Guest Pass',
  wallet_topup: 'Wallet Top-up',
  platform_subscription: 'Platform Subscription',
};

interface PaymentDescriptionInput {
  surface: PaymentDescriptionSurface;
  /**
   * The thing being paid for, in the customer's words: court name, class name,
   * package/plan name, guest name, tenant name. Never an id — a UUID here is
   * exactly the defect this module exists to prevent.
   */
  name?: string | null;
  /** ISO date (YYYY-MM-DD) for time-bound surfaces. */
  date?: string | null;
  /** Start time (HH:MM) for time-bound surfaces. */
  startTime?: string | null;
  /** Payer position for split payments, e.g. 2 of 4. */
  payerNumber?: number | null;
  totalPayers?: number | null;
  /**
   * True when the charge settles outstanding add-ons on an already-paid
   * booking/seat rather than the booking itself. Without this an add-on
   * top-up is indistinguishable from the original charge in the ledger.
   */
  isAddon?: boolean;
}

/** UUID (any version) in full or the 8-char prefix `.slice(0, 8)` produces. */
const UUID_LIKE =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{8})$/i;

/**
 * Drop values that would put an opaque id in the ledger. Callers sometimes
 * fall back to an id when a name lookup misses; better to omit the detail
 * entirely than to print `Class Registration - 21b95ec1`.
 */
function cleanName(name: string | null | undefined) {
  const trimmed = name?.trim();

  if (!trimmed || UUID_LIKE.test(trimmed)) {
    return null;
  }

  return trimmed;
}

function formatTime(time: string | null | undefined) {
  const trimmed = time?.trim();

  if (!trimmed) {
    return null;
  }

  // Normalize `14:30:00` → `14:30`; leave anything unexpected alone.
  const match = /^(\d{2}:\d{2})(?::\d{2})?$/.exec(trimmed);

  return match ? match[1] : trimmed;
}

/**
 * Build the gateway description for a payment.
 *
 * Shape: `<Surface> - <name>[ on <date>][ at <time>]`, with the surface label
 * always leading so the Payments list stays scannable and groupable. Missing
 * detail degrades gracefully to the bare label rather than emitting a dangling
 * separator or an id.
 */
export function buildPaymentDescription({
  surface,
  name,
  date,
  startTime,
  payerNumber,
  totalPayers,
  isAddon,
}: PaymentDescriptionInput): string {
  const label = SURFACE_LABELS[surface];
  const parts: string[] = [];

  if (surface === 'split_payment') {
    // Split payments are the one surface keyed by position rather than name.
    // Include the booking name when we have it so the row is attributable.
    const position =
      payerNumber && totalPayers
        ? `Payer ${payerNumber} of ${totalPayers}`
        : payerNumber
          ? `Payer ${payerNumber}`
          : null;

    if (position) parts.push(position);

    const cleaned = cleanName(name);
    if (cleaned) parts.push(cleaned);
  } else {
    const cleaned = cleanName(name);
    if (cleaned) parts.push(cleaned);
  }

  const cleanDate = date?.trim();
  if (cleanDate) parts.push(`on ${cleanDate}`);

  const time = formatTime(startTime);
  if (time) parts.push(`at ${time}`);

  // `Court Booking - Court 3 on 2026-08-31 at 14:30`
  const detail = parts.join(' ').trim();
  const base = detail ? `${label} - ${detail}` : label;

  return isAddon ? `${base} (add-ons)` : base;
}
